import { useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Layout } from "ui-components/Layout";
import { Success, Error_ } from "ui-components/Alert";
import { formRender, initialValues, skuValidations } from "./sku-schema";
import SKUInfo from "./components/SKUInfo2";
import SKUImage from "./components/SKUImage";
import { useLocation } from "react-router-dom";
import { getSKUByCode, editSKU, editSKUImage } from "services/data.internal.service";
import { SaveButton } from "ui-components/Form";
import { LoadingDialog } from "ui-components/Alert";

function EditSKU2() {
  const location = useLocation();
  const { customerCode, skuCode } = location.state;
  const [alert, setAlert] = useState(null);
  const [newImages, setNewImages] = useState([]);
  const [skuImages, setSKUImages] = useState([]);
  const [formValues, setFormValues] = useState(null);

  const queryClient = useQueryClient();

  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const { isFetching } = useQuery({
    queryKey: ["sku", { customerCode, skuCode }],
    queryFn: () => getSKUByCode({ customerCode, skuCode }),
    onSuccess: (data) => {
      const { skuImages: images, ...values } = data.data.data;
      setFormValues(values);
      setSKUImages(images);
    },
    onError,
  });

  const editSKUMutation = useMutation(editSKU, {
    onSuccess: (data) => setAlert(<Success data={data} onChange={setAlert} />),
    onError,
  });
  const editSKUImageMutation = useMutation(editSKUImage, {});
  const form = useForm({
    defaultValues: initialValues,
    values: formValues,
    resolver: yupResolver(skuValidations),
  });

  const editStatus = editSKUMutation.status === "loading";
  const editImageStatus = editSKUImageMutation.status === "loading";

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  const infoProps = {
    control,
    setValue,
    errors,
    formRender,
    mode: "EDIT",
  };
  const onSubmit = (data) => {
    window.scrollTo(0, 0);
    editSKUMutation.mutate(data, {
      onSuccess: () => {
        if (newImages.length > 0 || skuImages.length > 0) {
          let formData = new FormData();
          const deletedImage = skuImages.filter((x) => x.active === false);
          const defaultImage = newImages.filter((x) => x.default);
          const defaultSKUImage = skuImages.filter((x) => x.active === true && x.default === true);
          const isDefault =
            defaultImage.length > 0
              ? defaultImage[0].file.name
              : defaultSKUImage.length > 0
              ? defaultSKUImage[0].fileName
              : null;
          formData.append("skuCode", data.skuCode);
          formData.append("customerCode", data.customerCode);
          if (Boolean(isDefault)) {
            formData.append("defaultImage", isDefault);
          }
          if (newImages.length > 0) {
            newImages.forEach((image) => {
              formData.append("productImage", image.file, image.file.name);
            });
          } else {
            formData.append("productImage", null);
          }
          deletedImage.forEach((skuImage) => {
            formData.append("imageToDeleteIds", skuImage.id);
          });
          editSKUImageMutation.mutate(formData, {
            onSuccess: () => {
              setNewImages([]);
              queryClient.invalidateQueries(["sku"]);
            },
          });
        } else {
          queryClient.invalidateQueries(["sku"]);
        }
      },
    });
  };
  return (
    <Layout lg={12}>
      {(editStatus || editImageStatus || isFetching) && <LoadingDialog />}
      {alert}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <SKUInfo {...infoProps} />
        <SKUImage images={{ newImages, setNewImages, skuImages, setSKUImages, isEdit: true }} />
        <SaveButton label="save" disabled={isSubmitting} url="/setup/sku" />
      </form>
    </Layout>
  );
}

export default EditSKU2;
